import { type KeyboardEvent, useEffect, useState } from 'react'
import { ListFilter, Search, X } from 'lucide-react'
import {
  useV1ClassesList,
  useV1ReportingBudgetEntryOptionsRetrieve,
  useV1SchoolsList,
} from '@/api/client'
import { V1ClassesListState, V1SchoolsListState } from '@/api/model'
import { LookupTruncationNote } from '@/components/lookup-truncation-note'
import {
  EMPTY_STUDENT_DATA_REPORT_FILTERS,
  type StudentDataReportFilterValues,
} from '@/components/reporting/student-data-report-filter-values'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'
import { Field, FieldDescription, FieldError, FieldGroup, FieldLabel } from '@/components/ui/field'
import { Input } from '@/components/ui/input'
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { queryHasHttpError } from '@/lib/query-state'
import { reportFilterOptionLabel } from '@/lib/reporting-budget-entries'

const ALL = '__all__'

type FilterKey = keyof StudentDataReportFilterValues

interface FilterChip {
  key: FilterKey
  label: string
}

interface StudentDataReportFiltersProps {
  search: string
  onSearchChange: (value: string) => void
  filters: StudentDataReportFilterValues
  onFiltersChange: (filters: StudentDataReportFilterValues) => void
}

function activeFilterCount(filters: StudentDataReportFilterValues): number {
  return Object.entries(filters).filter(([key, value]) => {
    if (key === 'dateFrom' || key === 'dateTo') return value !== ''
    return value !== ALL
  }).length
}

function formatFilterDate(value: string): string {
  const [year, month, day] = value.split('-')
  return year && month && day ? `${day}.${month}.${year}` : value
}

function preventFormSubmitOnEnter(event: KeyboardEvent<HTMLInputElement>) {
  if (event.key === 'Enter') event.preventDefault()
}

export function StudentDataReportFilters({
  search,
  onSearchChange,
  filters,
  onFiltersChange,
}: StudentDataReportFiltersProps) {
  const [searchInput, setSearchInput] = useState(search)
  const [open, setOpen] = useState(false)
  const [draft, setDraft] = useState(filters)
  const [schoolSearchInput, setSchoolSearchInput] = useState('')
  const [schoolSearch, setSchoolSearch] = useState('')
  const [classSearchInput, setClassSearchInput] = useState('')
  const [classSearch, setClassSearch] = useState('')
  const [schoolLabels, setSchoolLabels] = useState<Record<string, string>>({})
  const [classLabels, setClassLabels] = useState<Record<string, string>>({})

  useEffect(() => {
    const timeout = setTimeout(() => {
      if (searchInput !== search) onSearchChange(searchInput)
    }, 300)
    return () => clearTimeout(timeout)
  }, [onSearchChange, search, searchInput])

  useEffect(() => {
    const timeout = setTimeout(() => {
      const nextSearch = schoolSearchInput.trim()
      if (nextSearch !== schoolSearch) setSchoolSearch(nextSearch)
    }, 300)
    return () => clearTimeout(timeout)
  }, [schoolSearch, schoolSearchInput])

  useEffect(() => {
    const timeout = setTimeout(() => {
      const nextSearch = classSearchInput.trim()
      if (nextSearch !== classSearch) setClassSearch(nextSearch)
    }, 300)
    return () => clearTimeout(timeout)
  }, [classSearch, classSearchInput])

  const schoolsQuery = useV1SchoolsList({
    state: V1SchoolsListState.all,
    ordering: 'name',
    page_size: 100,
    search: schoolSearch || undefined,
  })
  const schools = schoolsQuery.data?.status === 200 ? schoolsQuery.data.data.results : []
  const schoolsCount = schoolsQuery.data?.status === 200 ? schoolsQuery.data.data.count : 0
  const schoolsFailed = queryHasHttpError(schoolsQuery)

  const draftSchoolId = draft.schoolId === ALL ? undefined : Number(draft.schoolId)
  const classesQuery = useV1ClassesList(
    {
      state: V1ClassesListState.all,
      school: draftSchoolId,
      ordering: 'name',
      page_size: 100,
      search: classSearch || undefined,
    },
    { query: { enabled: draftSchoolId !== undefined } },
  )
  const classes = classesQuery.data?.status === 200 ? classesQuery.data.data.results : []
  const classesCount = classesQuery.data?.status === 200 ? classesQuery.data.data.count : 0
  const classesFailed = queryHasHttpError(classesQuery)

  const optionsQuery = useV1ReportingBudgetEntryOptionsRetrieve()
  const options = optionsQuery.data?.status === 200 ? optionsQuery.data.data : undefined
  const optionsFailed = queryHasHttpError(optionsQuery)

  const optionLabel = (
    entries: ReadonlyArray<{ value: string; label: string; is_active: boolean }>,
    value: string,
  ) => {
    const entry = entries.find((candidate) => candidate.value === value)
    return entry ? reportFilterOptionLabel(entry) : value
  }

  const chips: FilterChip[] = []
  if (filters.dateFrom)
    chips.push({ key: 'dateFrom', label: `Od ${formatFilterDate(filters.dateFrom)}` })
  if (filters.dateTo) chips.push({ key: 'dateTo', label: `Do ${formatFilterDate(filters.dateTo)}` })
  if (filters.schoolId !== ALL) {
    chips.push({
      key: 'schoolId',
      label: `Szkoła: ${schoolLabels[filters.schoolId] ?? schools.find((school) => String(school.id) === filters.schoolId)?.name ?? filters.schoolId}`,
    })
  }
  if (filters.classId !== ALL) {
    chips.push({
      key: 'classId',
      label: `Klasa: ${classLabels[filters.classId] ?? classes.find((klass) => String(klass.id) === filters.classId)?.name ?? filters.classId}`,
    })
  }
  if (filters.kind !== ALL) {
    chips.push({ key: 'kind', label: `Typ: ${optionLabel(options?.kinds ?? [], filters.kind)}` })
  }
  if (filters.incomeKind !== ALL) {
    chips.push({
      key: 'incomeKind',
      label: `Źródło: ${optionLabel(options?.income_kinds ?? [], filters.incomeKind)}`,
    })
  }
  if (filters.category !== ALL) {
    chips.push({
      key: 'category',
      label: `Kategoria: ${optionLabel(options?.categories ?? [], filters.category)}`,
    })
  }
  if (filters.classification !== ALL) {
    chips.push({
      key: 'classification',
      label: `Klasyfikacja: ${optionLabel(options?.classifications ?? [], filters.classification)}`,
    })
  }
  if (filters.emotion !== ALL) {
    chips.push({
      key: 'emotion',
      label: `Emocja: ${optionLabel(options?.emotions ?? [], filters.emotion)}`,
    })
  }

  const count = activeFilterCount(filters)
  const isFiltered = count > 0 || search.trim() !== ''

  const patchDraft = (patch: Partial<StudentDataReportFilterValues>) => {
    setDraft((current) => ({ ...current, ...patch }))
  }

  const resetLookupSearch = () => {
    setSchoolSearchInput('')
    setSchoolSearch('')
    setClassSearchInput('')
    setClassSearch('')
  }

  const removeFilter = (key: FilterKey) => {
    const next = {
      ...filters,
      [key]: key === 'dateFrom' || key === 'dateTo' ? '' : ALL,
    }
    if (key === 'schoolId') next.classId = ALL
    onFiltersChange(next)
  }

  const clearAll = () => {
    setSearchInput('')
    onSearchChange('')
    onFiltersChange(EMPTY_STUDENT_DATA_REPORT_FILTERS)
  }

  const dictionarySelect = (
    id: string,
    label: string,
    value: string,
    onValueChange: (value: string) => void,
    entries: ReadonlyArray<{ value: string; label: string; is_active: boolean }>,
  ) => (
    <Field>
      <FieldLabel htmlFor={id}>{label}</FieldLabel>
      <Select
        value={value}
        onValueChange={onValueChange}
        disabled={optionsQuery.isLoading || optionsFailed}
      >
        <SelectTrigger id={id} className="w-full">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectGroup>
            <SelectItem value={ALL}>Wszystkie</SelectItem>
            {entries.map((entry) => (
              <SelectItem key={entry.value} value={entry.value}>
                {reportFilterOptionLabel(entry)}
              </SelectItem>
            ))}
          </SelectGroup>
        </SelectContent>
      </Select>
    </Field>
  )

  return (
    <div className="flex min-w-0 flex-col gap-3">
      <div className="flex min-w-0 flex-col gap-2 md:flex-row md:items-end">
        <Field className="min-w-0 flex-1 gap-1">
          <FieldLabel htmlFor="report-search" className="sr-only">
            Szukaj w nazwie lub notatce
          </FieldLabel>
          <div className="relative w-full md:max-w-xl">
            <Search className="pointer-events-none absolute top-1/2 left-2.5 size-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              id="report-search"
              value={searchInput}
              onChange={(event) => setSearchInput(event.target.value)}
              placeholder="Szukaj w nazwie lub notatce…"
              className="pr-9 pl-8"
            />
            {searchInput ? (
              <Button
                type="button"
                variant="ghost"
                size="icon-sm"
                className="absolute top-1/2 right-1 -translate-y-1/2"
                aria-label="Wyczyść wyszukiwanie"
                onClick={() => {
                  setSearchInput('')
                  onSearchChange('')
                }}
              >
                <X />
              </Button>
            ) : null}
          </div>
        </Field>

        <div className="flex flex-wrap items-center gap-2">
          <Dialog
            open={open}
            onOpenChange={(nextOpen) => {
              setDraft(filters)
              if (!nextOpen) resetLookupSearch()
              setOpen(nextOpen)
            }}
          >
            <DialogTrigger asChild>
              <Button
                type="button"
                variant="outline"
                aria-label={count > 0 ? `Filtry: aktywnych ${count}` : undefined}
              >
                <ListFilter data-icon="inline-start" />
                {count > 0 ? `Filtry (${count})` : 'Filtry'}
              </Button>
            </DialogTrigger>
            <DialogContent className="max-h-[calc(100dvh-2rem)] overflow-y-auto sm:max-w-2xl">
              <form
                className="contents"
                onSubmit={(event) => {
                  event.preventDefault()
                  onFiltersChange(draft)
                  resetLookupSearch()
                  setOpen(false)
                }}
              >
                <DialogHeader>
                  <DialogTitle>Filtry raportu</DialogTitle>
                  <DialogDescription>
                    Zawęź wyniki po okresie, szkole, klasie i oznaczeniach wpisu.
                  </DialogDescription>
                </DialogHeader>

                {optionsQuery.isLoading ? (
                  <p className="text-sm text-muted-foreground" aria-live="polite">
                    Ładowanie słowników filtrów…
                  </p>
                ) : null}
                {optionsFailed ? (
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <FieldError>Nie udało się pobrać słowników filtrów.</FieldError>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => void optionsQuery.refetch()}
                    >
                      Spróbuj ponownie
                    </Button>
                  </div>
                ) : null}

                <FieldGroup className="grid gap-4 sm:grid-cols-2">
                  <Field>
                    <FieldLabel htmlFor="report-date-from">Data od</FieldLabel>
                    <Input
                      id="report-date-from"
                      type="date"
                      max={draft.dateTo || undefined}
                      value={draft.dateFrom}
                      onChange={(event) => patchDraft({ dateFrom: event.target.value })}
                    />
                  </Field>
                  <Field>
                    <FieldLabel htmlFor="report-date-to">Data do</FieldLabel>
                    <Input
                      id="report-date-to"
                      type="date"
                      min={draft.dateFrom || undefined}
                      value={draft.dateTo}
                      onChange={(event) => patchDraft({ dateTo: event.target.value })}
                    />
                  </Field>
                  <Field>
                    <FieldLabel htmlFor="report-school">Szkoła</FieldLabel>
                    <Input
                      value={schoolSearchInput}
                      onChange={(event) => setSchoolSearchInput(event.target.value)}
                      onKeyDown={preventFormSubmitOnEnter}
                      placeholder="Szukaj szkoły…"
                      aria-label="Szukaj szkoły"
                    />
                    <Select
                      value={draft.schoolId}
                      disabled={schoolsQuery.isLoading || schoolsFailed}
                      onValueChange={(value) => {
                        const selectedSchool = schools.find((school) => String(school.id) === value)
                        if (selectedSchool) {
                          setSchoolLabels((current) => ({
                            ...current,
                            [value]: selectedSchool.name,
                          }))
                        }
                        setClassSearchInput('')
                        setClassSearch('')
                        patchDraft({ schoolId: value, classId: ALL })
                      }}
                    >
                      <SelectTrigger id="report-school" className="w-full">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectGroup>
                          <SelectItem value={ALL}>Wszystkie szkoły</SelectItem>
                          {draft.schoolId !== ALL &&
                          schoolLabels[draft.schoolId] &&
                          !schools.some((school) => String(school.id) === draft.schoolId) ? (
                            <SelectItem value={draft.schoolId}>
                              {schoolLabels[draft.schoolId]}
                            </SelectItem>
                          ) : null}
                          {schools.map((school) => (
                            <SelectItem key={school.id} value={String(school.id)}>
                              {school.name}
                            </SelectItem>
                          ))}
                          {schoolSearch && schools.length === 0 ? (
                            <SelectItem value="__no_schools__" disabled>
                              Brak pasujących szkół
                            </SelectItem>
                          ) : null}
                          <LookupTruncationNote count={schoolsCount} shown={schools.length} />
                        </SelectGroup>
                      </SelectContent>
                    </Select>
                    {schoolsQuery.isLoading ? (
                      <FieldDescription>Ładowanie szkół…</FieldDescription>
                    ) : null}
                    {schoolsFailed ? (
                      <div className="flex flex-wrap items-center justify-between gap-2">
                        <FieldError>Nie udało się pobrać szkół.</FieldError>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => void schoolsQuery.refetch()}
                        >
                          Spróbuj ponownie
                        </Button>
                      </div>
                    ) : null}
                  </Field>
                  <Field
                    data-disabled={
                      draftSchoolId === undefined || classesQuery.isLoading || classesFailed
                    }
                  >
                    <FieldLabel htmlFor="report-class">Klasa</FieldLabel>
                    <Input
                      value={classSearchInput}
                      onChange={(event) => setClassSearchInput(event.target.value)}
                      onKeyDown={preventFormSubmitOnEnter}
                      placeholder="Szukaj klasy…"
                      aria-label="Szukaj klasy"
                      disabled={draftSchoolId === undefined}
                    />
                    <Select
                      value={draft.classId}
                      disabled={
                        draftSchoolId === undefined || classesQuery.isLoading || classesFailed
                      }
                      onValueChange={(value) => {
                        const selectedClass = classes.find((klass) => String(klass.id) === value)
                        if (selectedClass) {
                          setClassLabels((current) => ({
                            ...current,
                            [value]: selectedClass.name,
                          }))
                        }
                        patchDraft({ classId: value })
                      }}
                    >
                      <SelectTrigger id="report-class" className="w-full">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectGroup>
                          <SelectItem value={ALL}>Wszystkie klasy</SelectItem>
                          {draft.classId !== ALL &&
                          classLabels[draft.classId] &&
                          !classes.some((klass) => String(klass.id) === draft.classId) ? (
                            <SelectItem value={draft.classId}>
                              {classLabels[draft.classId]}
                            </SelectItem>
                          ) : null}
                          {classes.map((klass) => (
                            <SelectItem key={klass.id} value={String(klass.id)}>
                              {klass.name}
                            </SelectItem>
                          ))}
                          {classSearch && classes.length === 0 ? (
                            <SelectItem value="__no_classes__" disabled>
                              Brak pasujących klas
                            </SelectItem>
                          ) : null}
                          <LookupTruncationNote count={classesCount} shown={classes.length} />
                        </SelectGroup>
                      </SelectContent>
                    </Select>
                    {draftSchoolId === undefined ? (
                      <FieldDescription>Najpierw wybierz szkołę.</FieldDescription>
                    ) : null}
                    {draftSchoolId !== undefined && classesQuery.isLoading ? (
                      <FieldDescription>Ładowanie klas…</FieldDescription>
                    ) : null}
                    {draftSchoolId !== undefined && classesFailed ? (
                      <div className="flex flex-wrap items-center justify-between gap-2">
                        <FieldError>Nie udało się pobrać klas.</FieldError>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => void classesQuery.refetch()}
                        >
                          Spróbuj ponownie
                        </Button>
                      </div>
                    ) : null}
                  </Field>
                  {dictionarySelect(
                    'report-kind',
                    'Typ operacji',
                    draft.kind,
                    (value) => patchDraft({ kind: value }),
                    options?.kinds ?? [],
                  )}
                  {dictionarySelect(
                    'report-income-kind',
                    'Rodzaj przychodu',
                    draft.incomeKind,
                    (value) => patchDraft({ incomeKind: value }),
                    options?.income_kinds ?? [],
                  )}
                  {dictionarySelect(
                    'report-category',
                    'Kategoria',
                    draft.category,
                    (value) => patchDraft({ category: value }),
                    options?.categories ?? [],
                  )}
                  {dictionarySelect(
                    'report-classification',
                    'Klasyfikacja',
                    draft.classification,
                    (value) => patchDraft({ classification: value }),
                    options?.classifications ?? [],
                  )}
                  {dictionarySelect(
                    'report-emotion',
                    'Emocja',
                    draft.emotion,
                    (value) => patchDraft({ emotion: value }),
                    options?.emotions ?? [],
                  )}
                </FieldGroup>

                <DialogFooter className="sm:justify-between">
                  <Button
                    type="button"
                    variant="ghost"
                    onClick={() => {
                      setDraft(EMPTY_STUDENT_DATA_REPORT_FILTERS)
                      resetLookupSearch()
                    }}
                  >
                    Wyczyść pola
                  </Button>
                  <div className="flex flex-col-reverse gap-2 sm:flex-row">
                    <DialogClose asChild>
                      <Button type="button" variant="outline">
                        Anuluj
                      </Button>
                    </DialogClose>
                    <Button type="submit">Pokaż wyniki</Button>
                  </div>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>

          {isFiltered ? (
            <Button type="button" variant="ghost" onClick={clearAll}>
              Wyczyść wszystko
            </Button>
          ) : null}
        </div>
      </div>

      {chips.length > 0 ? (
        <div className="flex flex-wrap gap-1.5" aria-label="Aktywne filtry">
          {chips.map((chip) => (
            <Badge key={chip.key} variant="secondary" asChild>
              <button
                type="button"
                onClick={() => removeFilter(chip.key)}
                aria-label={`Usuń filtr: ${chip.label}`}
                title={chip.label}
                className="h-7 max-w-full min-w-0"
              >
                <span className="truncate">{chip.label}</span>
                <X data-icon="inline-end" />
              </button>
            </Badge>
          ))}
        </div>
      ) : null}
    </div>
  )
}
